import { useEffect, useMemo, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import CreatableSelect from "react-select/creatable";
import ChartNav from "../../../../common/ChartNav";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Vaccines from "../../vaccines/vaccines";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import SubNavigation from "../../common/sub-navigation";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import FollowUp from "../../service-steps/education/follow-up";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import { vaccineDoseTypeOptions } from "../clinical-assessment/data";
import MedicationCreate from "../clinical-assessment/MedicationCreate";
import CommonSelect from "../../services/select/common-creatable-select";
import { customReactSelectStyles } from "../../services/select/medication-select";
import CustomInput from "../../../../common/CustomInput";
import NewToolTip from "../../../../common/New-ToolTip";

/** Tooltip copy — Annual Medication Review / vaccine appointment modal (Figma 5729:22128, 5729:22248). */
const VACCINE_APPOINTMENT_INITIAL_SERIES_TOOLTIP =
  "Total planned doses for completion of series. Select this option if you wish to book initial and booster vaccine doses.";

/** Tooltip copy — booster dose type & ordinal field (Figma 5729:22722). */
const VACCINE_APPOINTMENT_BOOSTER_ORDINAL_TOOLTIP =
  "Select which booster dose this represents";

const vaccineAppointmentTooltipPanelClass =
  "max-w-[320px] rounded-[8px] bg-gray-20 p-2 text-left text-[14px] font-normal leading-5 text-teal-300 shadow-[0px_0px_7.5px_rgba(0,0,0,0.03)]";

const formatSelectOptionLabel = (option) => (
  <div>
    {option.label}
    {option.subLabel ? (
      <>
        <br />
        <p className="italic">{option.subLabel}</p>
      </>
    ) : null}
  </div>
);

/**
 * Same behavior/styling as CommonSelect (creatable) plus optional inline adornment after the value.
 * Local to vaccine appointment modal only — keeps shared CommonSelect unchanged.
 */
const VaccineAppointmentDoseTypeSelect = ({
  onChange = () => {},
  options: initialOptions = [],
  label = "Select Option",
  isRequired = false,
  value: actualValue = null,
  className = "",
  labelClassName = "",
  error = false,
  disabled = false,
  menuPlacement = "bottom",
  valueEndAdornment = null,
}) => {
  const [value, setValue] = useState(actualValue || null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setValue(actualValue?.value ? actualValue : null);
  }, [actualValue]);

  useEffect(() => {
    if (disabled || !isRequired) {
      setShowError(false);
      return;
    }
    setShowError(Boolean(error));
  }, [disabled, error, isRequired]);

  const selectStyles = useMemo(() => {
    const baseStyles = customReactSelectStyles(isRequired, value);

    return {
      ...baseStyles,
      valueContainer: (provided, state) => ({
        ...baseStyles.valueContainer(provided, state),
        paddingRight: valueEndAdornment ? "28px" : provided.paddingRight,
      }),
    };
  }, [isRequired, value, valueEndAdornment]);
  const selectedValueText = value?.label || value?.value || "";
  const selectContainerClass = `${className || "w-full col-span-8"} `;

  const handleCreate = (inputValue) => {
    const newOption = { label: inputValue, value: inputValue };
    setValue(newOption);
    setShowError(false);
    onChange(newOption);
  };

  const handleChange = (newValue) => {
    setValue(newValue);
    setShowError(isRequired && !newValue);
    onChange(newValue);
  };

  return (
    <div className={selectContainerClass}>
      <div
        className={`relative rounded-[4px] border-[1px] 
            ${disabled ? "pointer-events-none opacity-70" : ""} ${!disabled && isRequired && value === null ? "bg-blue-100" : "bg-white"} ${
          showError ? "border-red-200" : "border-gray-100"
        } px-3 py-3 focus-within:border-REELTeal focus-within:border-[2px]`}
      >
        <div>
          <span
            className={`block mb-1 text-gray-900 ${labelClassName || "text-body-xs"}`}
          >
            {label} {isRequired && <span className="">*</span>}{" "}
          </span>
          <CreatableSelect
            isClearable
            styles={selectStyles}
            isDisabled={disabled}
            onChange={handleChange}
            onCreateOption={handleCreate}
            onBlur={() => setShowError(!disabled && isRequired && !value)}
            options={initialOptions}
            value={value}
            placeholder={`Select ${label}`}
            className="col-span-4"
            menuPlacement={menuPlacement}
            formatOptionLabel={formatSelectOptionLabel}
          />
          {valueEndAdornment ? (
            <span
              className="pointer-events-none absolute left-3 top-[38px] z-20 inline-flex items-center gap-1 text-base leading-5 text-transparent"
            >
              <span aria-hidden="true">{selectedValueText}</span>
              <span className="pointer-events-auto inline-flex text-teal-300">
                {valueEndAdornment}
              </span>
            </span>
          ) : null}
        </div>
      </div>
      {showError && (
        <span className="block mt-1 text-red-200 text-body-xs">
          {label} is required
        </span>
      )}
    </div>
  );
};

const VaccineTableRadio = ({
  selected,
  onToggle,
  label,
  disabled = false,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-disabled={disabled}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      if (disabled) return;
      onToggle();
    }}
    className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[10px] border-[0.75px] focus:outline-none focus-visible:ring-2 focus-visible:ring-REELTeal disabled:pointer-events-none disabled:cursor-not-allowed ${
      disabled
        ? "border-gray-200 bg-gray-10 opacity-50"
        : selected
          ? "border-REELTeal bg-teal-50"
          : "border-gray-400 bg-white"
    }`}
  >
    {selected ? (
      <span
        className={`h-2 w-2 rounded-full ${disabled ? "bg-gray-400" : "bg-REELTeal"}`}
        aria-hidden
      />
    ) : null}
  </button>
);

const MAX_VACCINE_SERIES_DOSES = 5;

const getBoosterOrdinalLabel = (num) => {
  const n = Number(num);
  const suffix =
    n % 10 === 1 && n % 100 !== 11
      ? "st"
      : n % 10 === 2 && n % 100 !== 12
        ? "nd"
        : n % 10 === 3 && n % 100 !== 13
          ? "rd"
          : "th";
  return `${n}${suffix} Booster`;
};

const boosterSeriesMultiSelectOptions = Array.from(
  { length: MAX_VACCINE_SERIES_DOSES },
  (_, i) => {
    const value = i + 1;
    return { value, label: getBoosterOrdinalLabel(value) };
  }
);

const primarySeriesDoseCountOptions = Array.from(
  { length: MAX_VACCINE_SERIES_DOSES },
  (_, i) => {
    const v = String(i + 1);
    return { value: v, label: v };
  }
);

const isBoosterDoseTypeValue = (v) =>
  String(v || "").toLowerCase().includes("booster");

const isSingleDoseTypeValue = (v) => {
  const t = String(v || "").trim().toLowerCase();
  return t.includes("single") || t === "one dose";
};

const isInitialSeriesDoseTypeValue = (v) => {
  const t = String(v || "").trim().toLowerCase();
  return (
    t === "initial" ||
    t.includes("initial (series)") ||
    (t.includes("initial") && t.includes("series"))
  );
};

const normalizeTotalDosesForVaccinePayload = (vaccineDoseType, rawTotal) => {
  const dt = String(vaccineDoseType || "").toLowerCase();
  if (isSingleDoseTypeValue(vaccineDoseType)) {
    return "1";
  }
  if (dt.includes("booster")) {
    if (Array.isArray(rawTotal)) {
      if (rawTotal.length === 0) return "";
      return String(
        Math.min(MAX_VACCINE_SERIES_DOSES, rawTotal.length)
      );
    }
    const n = Number.parseInt(String(rawTotal ?? ""), 10);
    return String(
      Number.isNaN(n)
        ? 1
        : Math.min(MAX_VACCINE_SERIES_DOSES, Math.max(1, n))
    );
  }
  const n = Number.parseInt(String(rawTotal ?? ""), 10);
  if (Number.isNaN(n) || String(rawTotal ?? "").trim() === "") return "";
  return String(
    Math.min(MAX_VACCINE_SERIES_DOSES, Math.max(1, n))
  );
};

const normalizeBoosterOrdinalsSelection = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter(
          (n) =>
            Number.isFinite(n) && n >= 1 && n <= MAX_VACCINE_SERIES_DOSES
        )
    )
  ).sort((a, b) => a - b);

const getVaccineValueLabel = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    return (
      value.label ||
      value.value ||
      value.name ||
      value?.name?.label ||
      value?.name?.value ||
      ""
    );
  }
  return "";
};

const normalizeVaccineName = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (!value) return "";
  const nameValue = value.name ?? value.value ?? value.label;
  if (typeof nameValue === "string") return nameValue.trim();
  if (!nameValue || typeof nameValue !== "object") return "";
  const candidates = [
    nameValue.name,
    nameValue.value,
    nameValue.label,
    nameValue?.name?.name,
    nameValue?.name?.value,
    nameValue?.name?.label,
  ];
  return (
    candidates.find((entry) => typeof entry === "string" && entry.trim()) || ""
  );
};

const VaccineCards = ({
  showBack = false,
  onContinue,
  fromHealthAndWellness = false,
}) => {
  // State variables
  const {
    clinicalServiceObj,
    clinicalServiceName,
    clinicalServiceSteps,
    medications: clinicalServiceMedications = [],
  } =
    useSelector((state) => state.clinicalService);
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id, serviceId } = useParams();
  const dispatch = useDispatch();

  const [completeSubStep, setCompleteSubStep] = useState(true);
  const [showTable, setShowTable] = useState(false);

  // Vaccine(s) selected in this iteration
  const [selectedVaccines, setSelectedVaccines] = useState([]);
  const [selectedVaccineClass, setSelectedVaccineClass] = useState(null);

  const [focusedCard, setFocusedCard] = useState(null);
  const [navigateToBack, setNavigateToBack] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // For adding vaccines to service
  const [showConfirmModalAddVaccine, setShowConfirmModalAddVaccine] =
    useState(false);
  const [vaccineModalSelections, setVaccineModalSelections] = useState({});
  const serviceScopeRef = useRef(serviceId);

  const [showVaccineUpdateSlider, setShowVaccineUpdateSlider] = useState(false);
  const [vaccineUpdateQueue, setVaccineUpdateQueue] = useState([]);
  const [vaccineUpdateIndex, setVaccineUpdateIndex] = useState(0);

  // All vaccines confirmed and added to AMR
  const [confirmedVaccines, setConfirmedVaccines] = useState([]);

  /** Vaccine names whose history was saved via the Update & Continue / MedicationCreate slider. */
  const [updatedHistoryVaccineNames, setUpdatedHistoryVaccineNames] = useState(
    []
  );

  const [initialSelectedVaccines, setInitialSelectedVaccines] = useState([]);

  const [isNextDisabled, setIsNextDisabled] = useState(false);

  const [rowSelection, setRowSelection] = useState({});
  const [
    activeVaccineAppointmentTooltipId,
    setActiveVaccineAppointmentTooltipId,
  ] = useState(null);

  const dispatchServiceUpdate = (payload) => {
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      dispatch(updateClinicalService(serviceId, payload));
      return;
    }
    dispatch(updateMedicationReview(serviceId, payload));
  };

  const renderVaccineAppointmentTooltip = (tooltipId, message) => {
    const showTooltip = () =>
      setActiveVaccineAppointmentTooltipId(tooltipId);
    const hideTooltip = () => setActiveVaccineAppointmentTooltipId(null);
    const keepSelectClosed = (event) => {
      event.preventDefault();
      event.stopPropagation();
      showTooltip();
    };

    return (
      <>
        <span
          role="button"
          tabIndex={0}
          className="inline-flex cursor-pointer"
          data-tooltip-id={tooltipId}
          data-tooltip-place="bottom"
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          onPointerDown={keepSelectClosed}
          onMouseDown={keepSelectClosed}
          onClick={keepSelectClosed}
        >
          <ReactSVG
            src="/icons/information-dark.svg"
            className="h-5 w-5 shrink-0 text-teal-300"
          />
        </span>
        <NewToolTip
          isOpen={activeVaccineAppointmentTooltipId === tooltipId}
          id={tooltipId}
          place="bottom"
          positionStrategy="fixed"
          render={() => (
            <div className={vaccineAppointmentTooltipPanelClass}>
              {message}
            </div>
          )}
        />
      </>
    );
  };

  const resetVaccineSelections = () => {
    setConfirmedVaccines([]);
    setUpdatedHistoryVaccineNames([]);
    setSelectedVaccines([]);
    setInitialSelectedVaccines([]);
    setRowSelection({});
  };

  const vaccineList = [
    {
      id: 1,
      title: "Adults & Vaccines",
      description:
        "It is important for adults to keep vaccinations up to date because immunity from childhood vaccines can wear off over time.",
      icon: "adults-v",
    },
    {
      id: 2,
      title: "Travel & Vaccines",
      description:
        "Before you travel, take steps to prepare so you can stay safe and healthy during your trip.Make sure you are up-to-date on all of your routine vaccines.",
      icon: "travel-v",
    },
    {
      id: 3,
      title: "Pediatrics & Vaccines",
      description:
        "On-time vaccination is important because it helps provide immunity before children are exposed to potentially life-threatening diseases.",
      icon: "pediatrics-v",
    },
    {
      id: 4,
      title: "Pregnancy & Vaccines",
      description:
        "Vaccines during pregnancy give your baby some early protection too Antibodies can help protect your baby from diseases during the first few months of life.",
      icon: "pregnancy-v",
    },
    {
      id: 5,
      title: "Conditions & Vaccines",
      description:
        "You may need other vaccines based on your age and health conditions. Chronic medical conditions can increase your risk for diseases that can be prevented by vaccines.",
      icon: "conditions-v-2",
    },
  ];

  // State management
  useEffect(() => {
    let obj =
      clinicalServiceObj.initialAssessment.initialAssessmentForm.education;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      obj !== null &&
      obj !== undefined
    ) {
      if (obj.vaccineSchedule && obj.vaccineSchedule.length > 0) {
        const toVaccineKey = (value) =>
          String(value || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
        const medicationsFromAssessment =
          clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications
            ?.medications || [];
        const medicationPool = [
          ...(Array.isArray(clinicalServiceMedications)
            ? clinicalServiceMedications
            : []),
          ...(Array.isArray(medicationsFromAssessment)
            ? medicationsFromAssessment
            : []),
        ];
        const updatedMedicationKeys = new Set(
          medicationPool
            .filter(
              (item) =>
                String(item?.medicationType || "").trim().toLowerCase() ===
                "vaccine"
            )
            .map((item) => toVaccineKey(item?.medicationName))
            .filter(Boolean)
        );
        const validScheduleRows = obj.vaccineSchedule.filter(
          (x) => x?.name && records.some((record) => record.name === x.name)
        );
        const normalizedRows = validScheduleRows.map((row) => {
          const explicitSelection = row?.selectionType;
          const fallbackSelection = updatedMedicationKeys.has(
            toVaccineKey(row?.name)
          )
            ? "update"
            : "book";
          return {
            ...row,
            selectionType:
              explicitSelection === "update" || explicitSelection === "book"
                ? explicitSelection
                : fallbackSelection,
          };
        });
        const vaccineNames = normalizedRows.map((x) => x.name);
        const updateNames = normalizedRows
          .filter((x) => x?.selectionType === "update")
          .map((x) => x.name);
        const bookNames = normalizedRows
          .filter((x) => x?.selectionType !== "update")
          .map((x) => x.name);

        setSelectedVaccines([]);
        setConfirmedVaccines(bookNames);
        setUpdatedHistoryVaccineNames(updateNames);
        // Keep booking dirty-check scoped to "book" choices only.
        setInitialSelectedVaccines(bookNames);
        setRowSelection(
          Object.fromEntries(
            vaccineNames.map((n) => [n, updateNames.includes(n) ? "update" : "book"])
          )
        );
      } else {
        resetVaccineSelections();
      }
    } else {
      resetVaccineSelections();
    }
  }, [
    clinicalServiceObj.initialAssessment.initialAssessmentForm.education,
    clinicalServiceObj.initialAssessment.initialAssessmentForm.medications,
    clinicalServiceMedications,
    fromHealthAndWellness,
  ]);

  // Reset Next button state when component loads or fromHealthAndWellness changes
  useEffect(() => {
    if (fromHealthAndWellness) {
      setIsNextDisabled(false);
    }
  }, [fromHealthAndWellness]);

  // Monitor selected vaccines to control Next button state
  useEffect(() => {
    if (fromHealthAndWellness) {
      setIsNextDisabled(selectedVaccines.length > 0);
    }
  }, [selectedVaccines, fromHealthAndWellness]);

  const followUpUpdateNames = () =>
    Object.entries(rowSelection)
      .filter(([, v]) => v === "update")
      .map(([k]) => k);

  const followUpBookNames = () =>
    Object.entries(rowSelection)
      .filter(([, v]) => v === "book")
      .map(([k]) => k);

  /** Update actions that are newly selected in this view (exclude already completed updates). */
  const getPendingUpdateNames = () =>
    followUpUpdateNames().filter(
      (name) => !updatedHistoryVaccineNames.includes(name)
    );

  /** Vaccines that still need dose/series in the confirm modal (already confirmed are excluded). */
  const getVaccinesForConfirmModal = () => {
    const base =
      selectedVaccines.length > 0 ? selectedVaccines : followUpBookNames();
    return base.filter((name) => !confirmedVaccines.includes(name));
  };

  const followUpBookDirty = () => {
    const sorted = [...followUpBookNames()].sort();
    const initialSorted = [...initialSelectedVaccines].sort();
    return (
      sorted.length !== initialSorted.length ||
      !sorted.every((v, i) => v === initialSorted[i])
    );
  };

  const showFollowUpUpdateContinue = getPendingUpdateNames().length > 0;

  const showFollowUpSaveContinue =
    getPendingUpdateNames().length === 0 && followUpBookDirty();

  const pendingBookNames = followUpBookNames().filter(
    (name) => !confirmedVaccines.includes(name)
  );
  const pendingUpdateNames = getPendingUpdateNames();
  const hasAnyUpdateSelection = followUpUpdateNames().length > 0;
  /** Mute entire Update column when there is a newly selected Book action. */
  const followUpUpdateColumnMuted = pendingBookNames.length > 0;
  /** Keep Book column muted when any update selection exists (including persisted updates). */
  const followUpBookColumnMuted = hasAnyUpdateSelection;
  /** Cross-column lock for follow-up radios (pending selections only). */
  const followUpHasAnyBookSelection = pendingBookNames.length > 0;
  const followUpHasAnyUpdateSelection = pendingUpdateNames.length > 0;

  /** One choice per vaccine row: "update" | "book" (mutually exclusive). */
  const setRowChoice = (vaccineName, column) => {
    setRowSelection((prev) => {
      const cur = prev[vaccineName];
      const next = { ...prev };
      if (cur === column) {
        delete next[vaccineName];
      } else {
        next[vaccineName] = column;
      }
      return next;
    });
  };

  // Move back and forward
  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );
      const updatedClinicalServiceObj = {
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        serviceSteps: clinicalServiceSteps,
      };

      dispatchServiceUpdate(updatedClinicalServiceObj);
    }
  };

  // Save to DB
  const saveVaccines = async () => {
    await saveVaccinesToBackend();
  };

  // Handle save and continue
  const handleSaveAndContinue = () => {
    const pendingForModal = getVaccinesForConfirmModal();
    if (pendingForModal.length === 0) {
      return;
    }
    if (fromHealthAndWellness) {
      setVaccineModalSelections((prev) => {
        const next = { ...prev };
        pendingForModal.forEach((name) => {
          delete next[name];
        });
        return next;
      });
    }
    setShowConfirmModalAddVaccine(true);
  };

  // Handle closing the confirm popup
  const handleCancelAddingVaccine = () => {
    setShowConfirmModalAddVaccine(false);
    if (fromHealthAndWellness) {
      setSelectedVaccines([]);
      setIsNextDisabled(false);
    }
  };

  // Handle confirm and continue
  const handleAddVaccineToService = async () => {
    const vaccinesForConfirm = getVaccinesForConfirmModal();
    const newConfirmedVaccines = [
      ...new Set([...confirmedVaccines, ...vaccinesForConfirm]),
    ];
    const vaccineDetailsByName = Object.fromEntries(
      newConfirmedVaccines.map((name) => {
        const selected = vaccineModalSelections[name] || {};
        const vaccineDoseType = String(selected.vaccineDoseType || "").trim();
        const totalNumberOfVaccineDoses = normalizeTotalDosesForVaccinePayload(
          vaccineDoseType,
          selected.totalNumberOfVaccineDoses
        );
        const selectedBoosterOrdinals =
          isBoosterDoseTypeValue(vaccineDoseType) &&
          Array.isArray(selected.totalNumberOfVaccineDoses)
            ? selected.totalNumberOfVaccineDoses
                .map((option) => Number(option))
                .filter((value) => Number.isFinite(value))
                .sort((a, b) => a - b)
            : [];
        const seriesCountForPopup = isBoosterDoseTypeValue(vaccineDoseType)
          ? selectedBoosterOrdinals.length || 1
          : Number.parseInt(String(totalNumberOfVaccineDoses || ""), 10) || 1;
        return [
          name,
          {
            vaccineDoseType: vaccineDoseType || null,
            totalNumberOfVaccineDoses:
              totalNumberOfVaccineDoses && totalNumberOfVaccineDoses !== "0"
                ? totalNumberOfVaccineDoses
                : null,
            selectedBoosterOrdinals,
            seriesCountForPopup,
          },
        ];
      })
    );
    const selectedVaccinesSnapshot = vaccinesForConfirm.map((name) => {
      const selected = vaccineModalSelections[name] || {};
      const normalizedTotal = normalizeTotalDosesForVaccinePayload(
        selected.vaccineDoseType,
        selected.totalNumberOfVaccineDoses
      );
      const base = {
        name,
        ...(vaccineDetailsByName[name] || {}),
        seriesCountForPopup: Number.parseInt(String(normalizedTotal || ""), 10) || 1,
      };
      if (isBoosterDoseTypeValue(selected.vaccineDoseType)) {
        if (
          Array.isArray(selected.totalNumberOfVaccineDoses) &&
          selected.totalNumberOfVaccineDoses.length > 0
        ) {
          base.seriesLabelsForPopup = selected.totalNumberOfVaccineDoses
            .map((o) => Number(o))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b)
            .map((ord) => getBoosterOrdinalLabel(ord));
        }
      } else if (!isSingleDoseTypeValue(selected.vaccineDoseType)) {
        const totalCount = Math.max(
          1,
          Number.parseInt(String(base.seriesCountForPopup || 1), 10) || 1
        );
        base.seriesLabelsForPopup = Array.from({ length: totalCount }, (_, idx) =>
          idx === 0 ? "Initial Dose" : getBoosterOrdinalLabel(idx)
        );
      }
      if (
        isBoosterDoseTypeValue(selected.vaccineDoseType) &&
        Array.isArray(selected.totalNumberOfVaccineDoses) &&
        selected.totalNumberOfVaccineDoses.length > 0
      ) {
        base.selectedBoosterOrdinals = selected.totalNumberOfVaccineDoses
          .map((o) => Number(o))
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b);
        base.seriesCountForPopup = base.selectedBoosterOrdinals.length || 1;
      }
      return base;
    });
    try {
      // Save to backend first; only update UI state on success.
      await saveVaccinesToBackend(
        newConfirmedVaccines,
        vaccineDetailsByName,
        Object.fromEntries(vaccinesForConfirm.map((name) => [name, "book"]))
      );
      setShowConfirmModalAddVaccine(false);
      setConfirmedVaccines((prev) => [...prev, ...vaccinesForConfirm]);
      setRowSelection((prev) => {
        const next = { ...prev };
        vaccinesForConfirm.forEach((name) => {
          next[name] = "book";
        });
        return next;
      });
      setSelectedVaccines([]);
      if (fromHealthAndWellness) {
        setIsNextDisabled(false);
      }
    } catch (error) {
      console.error("Vaccine schedule save failed in confirmation modal.", {
        vaccineCount: vaccinesForConfirm.length,
      });
      return;
    }
    // Flow split: from Follow-up return with payload, else stay in service step flow.
    if (showBack && typeof onContinue === "function") {
      setShowTable(false);
      onContinue(selectedVaccinesSnapshot);
      return;
    }
    // In Health & Wellness flow, confirming keeps user on this screen for manual navigation.
  };

  // Save vaccines to backend
  const saveVaccinesToBackend = async (
    vaccinestoSave = confirmedVaccines,
    vaccineDetailsByName = {},
    vaccineSelectionByName = {}
  ) => {
    const currentVaccines =
      clinicalServiceObj.initialAssessment.initialAssessmentForm.education
        ?.vaccineSchedule;

    const keptVaccines = (
      currentVaccines?.map((v) => {
        const normalizedName = normalizeVaccineName(v);
        const base = v && typeof v === "object" ? v : {};
        return { ...base, name: normalizedName };
      })
        .filter((v) => v.name && vaccinestoSave.includes(v.name))
        .map((v) => {
          const details = vaccineDetailsByName[v.name];
          if (!details) return v;
          return {
            ...v,
            vaccineDoseType: details.vaccineDoseType ?? v.vaccineDoseType ?? null,
            totalNumberOfVaccineDoses:
              details.totalNumberOfVaccineDoses ??
              v.totalNumberOfVaccineDoses ??
              null,
            selectedBoosterOrdinals:
              details.selectedBoosterOrdinals ??
              v.selectedBoosterOrdinals ??
              [],
            seriesCountForPopup:
              details.seriesCountForPopup ?? v.seriesCountForPopup ?? null,
            selectionType:
              vaccineSelectionByName[v.name] ?? v.selectionType ?? "book",
          };
        }) || []
    );

    const existingNames = keptVaccines.map((item) => item.name);

    // Add new vaccines that aren't already in the kept vaccines
    const newAppointments = vaccinestoSave
      .filter((name) => !existingNames.includes(name))
      .map((name) => ({
        name: typeof name === 'string' ? name : String(name),
        bookingDate: null,
        vaccineDoseType: vaccineDetailsByName[name]?.vaccineDoseType ?? null,
        totalNumberOfVaccineDoses:
          vaccineDetailsByName[name]?.totalNumberOfVaccineDoses ?? null,
        selectedBoosterOrdinals:
          vaccineDetailsByName[name]?.selectedBoosterOrdinals ?? [],
        seriesCountForPopup:
          vaccineDetailsByName[name]?.seriesCountForPopup ?? null,
        selectionType: vaccineSelectionByName[name] ?? "book",
      }));

    // Final list contains only kept vaccines + new vaccines (all with normalized names)
    const updatedAppointments = [...keptVaccines, ...newAppointments];

    const updatedSteps = {
      education: {
        vaccineSchedule: updatedAppointments,
      },
      currentStep: currentStep,
      currentSubStep: currentSubStep,
    };

    // Persist vaccine schedule on the clinical service only. Patient vaccine rows
    // for Vaccine List / VaccineTable come from MedicationCreate (Update Vaccine History).
    dispatchServiceUpdate(updatedSteps);
  };

  const handleUpdateAndContinue = () => {
    const updateNames = getPendingUpdateNames();
    const selected = new Set(updateNames);
    const inTableOrder = records
      .map((r) => r.name)
      .filter((n) => selected.has(n));
    const queue = inTableOrder.length ? inTableOrder : [...updateNames].sort();
    setVaccineUpdateQueue(queue);
    setVaccineUpdateIndex(0);
    setShowVaccineUpdateSlider(true);
  };

  const finalizeVaccineUpdateFromSlider = async () => {
    const updateNames = getPendingUpdateNames();
    const bookNames = followUpBookNames();
    const persisted = [...new Set([...bookNames, ...updateNames])];
    const selectionByName = {
      ...Object.fromEntries(bookNames.map((n) => [n, "book"])),
      ...Object.fromEntries(updateNames.map((n) => [n, "update"])),
    };
    try {
      await saveVaccinesToBackend(persisted, {}, selectionByName);
    } catch (error) {
      return;
    }
    if (updateNames.length > 0) {
      setUpdatedHistoryVaccineNames((prev) => [
        ...new Set([...prev, ...updateNames]),
      ]);
    }
    setRowSelection(
      Object.fromEntries(persisted.map((n) => [n, selectionByName[n] || "book"]))
    );
    setShowTable(false);
    setShowVaccineUpdateSlider(false);
    setVaccineUpdateQueue([]);
    setVaccineUpdateIndex(0);
  };

  const handleVaccineMedicationSave = async () => {
    if (!vaccineUpdateQueue.length) {
      await finalizeVaccineUpdateFromSlider();
      return;
    }
    if (vaccineUpdateIndex < vaccineUpdateQueue.length - 1) {
      setVaccineUpdateIndex((i) => i + 1);
      return;
    }
    await finalizeVaccineUpdateFromSlider();
  };

  const onPrevious = () => {
    resetComponent();
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatchServiceUpdate(updatedSteps);
  };

  const resetComponent = () => {};

  useEffect(() => {
    // Access the DOM element using the current property of the ref
    const myElement = document.getElementById("cards");
    if (myElement) {
      setIsOverflowing(myElement.scrollWidth > myElement.clientWidth);
      setEnableRightArrow(true);
    }
  }, [VaccineCards]);

  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const handleLeftScroll = () => {
    const element = document.getElementById("cards");
    element.scrollLeft -= 400;
    // at top right scroll - scrollLeft + clientWidth will be equal to scrollWidth
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    // at top left scroll - scrollLeft will be equal to 0
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const [enableRightArrow, setEnableRightArrow] = useState(false);
  const handleRightScroll = () => {
    const element = document.getElementById("cards");
    element.scrollLeft += 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const records = [
    {
      name: "Diphtheria, Tetanus",
      recomendations: [
        "Primary series for previously unimmunized adults",
        "Booster dose every 10 years",
      ],
    },
    {
      name: "Herpes Zoster (shingles)",
      recomendations: [
        "50 years of age and older - 2 doses RZV",
        "50 years of age and older previously received LZV - 2 doses RZV, at least 1 year after immunization with LZV",
        "50 years of age and older previous episode of HZ - 2 doses RZV, at least 1 year after episode of HZ",
      ],
    },
    {
      name: "Human Papillomavirus (HPV)",
      recomendations: [
        "Women up to and including 26 years of age - bivalent (HPV2) or nonavalent (HPV9) vaccine",
        "Men up to and including 26 years of age - HPV9 vaccine",
      ],
    },
    {
      name: "Influenza",
      recomendations: ["Annually"],
    },
    {
      name: "Measles, Mumps",
      recomendations: [
        "Susceptible adults born in or after 1970 - 1 dose",
        "Born before 1970 - consider immune",
      ],
    },
    {
      name: "Meningococcal Conjugate",
      recomendations: [
        "Adults up to and including 24 years of age not immunized in adolescence - 1 dose",
      ],
    },
    {
      name: "Pertussis",
      recomendations: [
        "One dose of acellular pertussis-containing vaccine in adulthood",
        "Adults who will be in close contact with young infants should be immunized as early as possible",
        "One dose of Tdap vaccine should be administered in every pregnancy, ideally between 27 and 32 weeks of gestation.",
      ],
    },
    {
      name: "Pneumococcal Polysaccharide 23-Valent",
      recomendations: ["65 years of age and older - 1 dose"],
    },
    {
      name: "Polio",
      recomendations: [
        "Primary series for previously unimmunized adults when a primary series of tetanus toxoid- and diphtheria toxoid- containing vaccine is being given or with routine tetanus toxoid- and diphtheria toxoid- containing vaccine booster doses",
      ],
    },
    {
      name: "Rubella",
      recomendations: [
        "Susceptible adults - 1 dose",
        "If vaccine is indicated, pregnant women should be immunized after delivery",
      ],
    },
    {
      name: "Varicella (Chickenpox)",
      recomendations: [
        "Susceptible adults up to and including 49 years of age - 2 doses; if only one dose was previously received, a second dose should be provided",
        "Known seronegative adults 50 years of age and older - 2 doses - routine testing is not advised",
      ],
    },
  ];

  const normalizeVaccineKey = (value) =>
    String(getVaccineValueLabel(value) || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const vaccineScheduleByName = Object.fromEntries(
    (
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.education
        ?.vaccineSchedule || []
    )
      .map((item) => {
        const name = getVaccineValueLabel(item?.name);
        return name ? [name, item] : null;
      })
      .filter(Boolean)
  );

  const vaccineMedicationByKey = Object.fromEntries(
    (Array.isArray(clinicalServiceMedications) ? clinicalServiceMedications : [])
      .filter(
        (item) =>
          String(item?.medicationType || "").trim().toLowerCase() === "vaccine"
      )
      .map((item) => {
        const key = normalizeVaccineKey(item?.medicationName);
        return key ? [key, item] : null;
      })
      .filter(Boolean)
  );

  const vaccineDoseTypeSuggestionOptions = [
    ...new Set(
      [
        ...vaccineDoseTypeOptions.map((option) => option.value),
        ...Object.values(vaccineScheduleByName)
          .map((item) => getVaccineValueLabel(item?.vaccineDoseType))
          .filter(Boolean),
      ]
    ),
  ];

  const getVaccineDoseTypeOptionLabel = (value) =>
    vaccineDoseTypeOptions.find((option) => option.value === value)?.label ||
    value;

  const totalDoseOptions = [
    ...new Set(
      Object.values(vaccineScheduleByName)
        .map((item) => getVaccineValueLabel(item?.totalNumberOfVaccineDoses))
        .filter(Boolean)
    ),
  ];

  const mergedPrimaryDoseCountOptions = [
    ...new Set([
      ...primarySeriesDoseCountOptions.map((o) => o.value),
      ...totalDoseOptions
        .map((x) => String(x).trim())
        .filter(
          (x) =>
            /^\d+$/.test(x) &&
            Number(x) >= 1 &&
            Number(x) <= MAX_VACCINE_SERIES_DOSES
        ),
    ]),
  ]
    .map((v) => String(Number(v)))
    .sort((a, b) => Number(a) - Number(b))
    .map((v) => ({ label: v, value: v }));

  const getDoseCountLabel = (vaccineName) => {
    const doseType = String(
      vaccineModalSelections[vaccineName]?.vaccineDoseType || ""
    ).toLowerCase();
    return doseType.includes("booster")
      ? "Number of Booster Doses In Series"
      : "Number of Vaccine Doses In Series";
  };

  useEffect(() => {
    if (!showConfirmModalAddVaccine) return;
    const vaccinesForConfirm = getVaccinesForConfirmModal();
    const shouldPrefillFromPersistedSchedule = !showBack && !fromHealthAndWellness;
    const resolveModalFieldValue = (existingValue, scheduledValue) => {
      const normalizedExisting = String(existingValue ?? "").trim();
      if (normalizedExisting) return normalizedExisting;
      if (!shouldPrefillFromPersistedSchedule) return "";
      const normalizedScheduled = String(scheduledValue ?? "").trim();
      return normalizedScheduled || "";
    };
    setVaccineModalSelections((prev) => {
      const next = { ...prev };
      vaccinesForConfirm.forEach((name) => {
        const scheduleItem = vaccineScheduleByName[name];
        const medicationItem = vaccineMedicationByKey[normalizeVaccineKey(name)];
        const existing = next[name] || {};
        const vaccineDoseType = resolveModalFieldValue(
          existing.vaccineDoseType,
          getVaccineValueLabel(scheduleItem?.vaccineDoseType) ||
            getVaccineValueLabel(medicationItem?.vaccineDoseType)
        );
        const rawTotal = resolveModalFieldValue(
          existing.totalNumberOfVaccineDoses,
          getVaccineValueLabel(scheduleItem?.totalNumberOfVaccineDoses) ||
            getVaccineValueLabel(medicationItem?.totalNumberOfVaccineDoses)
        );
        let totalNumberOfVaccineDoses = rawTotal;
        if (isBoosterDoseTypeValue(vaccineDoseType)) {
          const count = Number.parseInt(String(rawTotal), 10);
          const ordinalsFromSchedule = normalizeBoosterOrdinalsSelection(
            scheduleItem?.selectedBoosterOrdinals ||
              medicationItem?.selectedBoosterOrdinals ||
              []
          );
          if (ordinalsFromSchedule.length > 0) {
            totalNumberOfVaccineDoses = ordinalsFromSchedule;
          } else if (!Number.isNaN(count) && count > 0) {
            totalNumberOfVaccineDoses = boosterSeriesMultiSelectOptions
              .filter((o) => o.value <= Math.min(MAX_VACCINE_SERIES_DOSES, count))
              .map((o) => o.value);
          } else {
            totalNumberOfVaccineDoses = [];
          }
        } else if (isSingleDoseTypeValue(vaccineDoseType)) {
          totalNumberOfVaccineDoses = "1";
        }
        next[name] = {
          vaccineDoseType,
          totalNumberOfVaccineDoses,
        };
      });
      return next;
    });
  }, [showConfirmModalAddVaccine, selectedVaccines, showBack, fromHealthAndWellness, confirmedVaccines]);

  // Keep modal values for same service; clear only when service record changes.
  useEffect(() => {
    if (serviceScopeRef.current !== serviceId) {
      serviceScopeRef.current = serviceId;
      setVaccineModalSelections({});
    }
  }, [serviceId]);

  return navigateToBack ? (
    <FollowUp />
  ) : (
    <div className="flex flex-col flex-1 gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Vaccines />
          <div className="flex flex-1 w-[1420px] h-[660px]">
            <div className="w-full gap-5 h-full overflow-hidden">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-2xl font-bold text-teal-600">
                  Vaccine Health & Prevention
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              {!showTable && (
                <div className="overflow-y-auto">
                  {!showBack && (
                    <h4 className="text-[22px] font-semibold text-teal-600 gap-6 mb-[20px]">
                      Vaccines based on age, health conditions, job, lifestyle
                      or travel habits.
                    </h4>
                  )}
                  {showBack && (
                    <button
                      className="flex items-center gap-2 w-fit mb-5"
                      onClick={() => {
                        setNavigateToBack(true);
                      }}
                    >
                      <ChevronLeftIcon className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-semibold text-teal-600 hover:underline">
                        Back to Follow Up Appointments
                      </h3>
                    </button>
                  )}
                  <div
                    className="flex flex-row pb-[50px] overflow-x-auto scroll-smooth gap-6 whitespace-normal min-w-[600px]"
                    id="cards"
                  >
                    {vaccineList.map((item, i) => (
                      <div
                        className={`h-[480px] min-w-[395px] pl-[12px] pt-[14px] rounded-[12px] border-[7px] gap-[10px] ${
                          focusedCard === i
                            ? "border-REELTeal"
                            : "border-blue-200 hover:border-gray-50"
                        }`}
                      >
                        {/* <ReactSVG src={"/icons/border.svg"} className={``} /> */}
                        <div
                          className="flex flex-col justify-between h-[430px] w-[360px] rounded-[12px] pt-10 pb-6 px-5 border-b-gray-100 border-x-gray-100 border-[1px] shadow-lg shadow-gray-400"
                          onClick={() => {
                            setFocusedCard(i);
                          }}
                          // onMouseEnter={() => {
                          //   setFocusedCard(i);
                          // }}
                        >
                          <div className="flex flex-col h-[320px] gap-4">
                            <div className="flex items-center justify-center pb-2">
                              <ReactSVG
                                src={`/icons/${item.icon}.svg`}
                                className={``}
                              />
                            </div>
                            <div className="flex flex-col text-gray-800 gap-[4px]">
                              <h4 className="text-[22px] font-semibold">
                                {item.title}
                              </h4>
                              <p className="text-base font-medium">
                                {item.description}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`flex flex-col gap-[8px] items-end justify-end`}
                          >
                            {item.id === 1 && (
                              <button
                                className="flex-row gap-2 disabled:opacity-40 h-10 w-[190px] rounded-full bg-REELTeal border-REELTeal hover:bg-teal-200  text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-6 py-2"
                                onClick={() => {
                                  setShowTable(true);
                                  setSelectedVaccineClass("Adults & Vaccines");
                                }}
                              >
                                <h3 className="text-base font-semibold text-white">
                                  View Vaccines
                                </h3>
                                <ChevronRightIcon className="w-4 h-4 text-white" />
                              </button>
                            )}
                            {item.id !== 1 && (
                              <Button
                                className="min-w-[200px]"
                                text="Coming soon!"
                                type="primary-outline"
                                onClick={() => {}}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {isOverflowing && (
                      <>
                        {enableLeftArrow && (
                          <button
                            className="absolute m-auto bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                            onClick={handleLeftScroll}
                          >
                            <ReactSVG
                              src="/icons/arrow-right.svg"
                              className="w-[15px] rotate-180"
                            />
                          </button>
                        )}
                        {enableRightArrow && (
                          <button
                            className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                            onClick={handleRightScroll}
                          >
                            <ReactSVG
                              src="/icons/arrow-right.svg"
                              className="w-[15px]"
                            />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {showTable && (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <button
                      className="flex items-center gap-2 w-fit"
                      onClick={() => {
                        setShowTable(false);
                        saveVaccines();
                      }}
                    >
                      <ChevronLeftIcon className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-semibold text-teal-600 hover:underline">
                        Back to {selectedVaccineClass}
                      </h3>
                    </button>
                    <p className="pl-6 text-gray-800">
                      It is important for adults to keep vaccinations up to date
                      because immunity from childhood vaccines can wear off over
                      time.
                    </p>
                  </div>
                  {records.length > 0 && (
                    <div className="flex flex-col rounded-[4px] ml-3 border border-gray-100 h-[520px] overflow-y-auto scroll-smooth">
                      <table className="min-w-full bg-gray-50 divide-y">
                        <thead className="sticky top-0 bg-gray-50 z-10">
                          <tr>
                            <th
                              scope="col"
                              className="py-2 pl-4 w-[40%] text-base font-semibold text-left text-gray-800"
                            >
                              Vaccine
                            </th>
                            <th
                              scope="col"
                              className="py-2 w-[85%] text-base font-semibold text-left text-gray-800 pr-8"
                            >
                              Recommendations for routine immunization
                            </th>
                            <th
                              scope="col"
                              className="py-2 min-w-[250px] text-base font-semibold text-center text-gray-800 relative border-l border-gray-100"
                            >
                              Update Vaccine History
                            </th>
                            <th
                              scope="col"
                              className="py-2 min-w-[250px] text-base font-semibold text-center text-gray-800 border-l border-gray-100"
                            >
                              Book Appointment
                            </th>
                          </tr>
                        </thead>

                        <tbody className="bg-white divide-y">
                          {records.map((vac, i) => (
                            <tr
                              className="transition-colors duration-300 h-[70px] cursor-pointer group bg-white hover:bg-gray-20"
                              key={i}
                            >
                              <td className="pl-4 py-2 w-[20%] text-base font-semibold text-gray-700">
                                {vac.name}
                              </td>
                              <td className="w-[85%] py-2 text-base font-normal text-gray-700 pr-10">
                                <ul className="pl-6 list-disc">
                                  {vac.recomendations.map(
                                    (recommendation, index) => (
                                      <li key={index}>{recommendation}</li>
                                    )
                                  )}
                                </ul>
                              </td>
                              <td
                                className={`text-center py-2 min-w-[250px] relative align-middle border-l border-gray-100 ${
                                  followUpUpdateColumnMuted ? "bg-gray-20" : ""
                                }`}
                              >
                                <div className="flex justify-center">
                                  {updatedHistoryVaccineNames.includes(
                                    vac.name
                                  ) ? (
                                    <span className="inline-flex items-center rounded-[4px] border border-teal-100 bg-teal-50 px-[6px] py-[1px] text-[12px] font-normal leading-[18px] text-teal-300">
                                      Updated
                                    </span>
                                  ) : (
                                    <VaccineTableRadio
                                      label={`Update vaccine history for ${vac.name}`}
                                      selected={
                                        rowSelection[vac.name] === "update"
                                      }
                                      disabled={
                                        followUpHasAnyBookSelection &&
                                        rowSelection[vac.name] !== "update"
                                      }
                                      onToggle={() =>
                                        setRowChoice(vac.name, "update")
                                      }
                                    />
                                  )}
                                </div>
                                <div className="absolute right-0 top-0 bottom-0 w-px border-r border-gray-200 h-full" />
                              </td>
                              <td
                                className={`text-center py-2 min-w-[250px] align-middle border-l border-gray-100 ${
                                  followUpBookColumnMuted ? "bg-gray-20" : ""
                                }`}
                              >
                                <div className="flex justify-center">
                                  {confirmedVaccines.includes(vac.name) ? (
                                    <span className="inline-flex items-center rounded-[4px] border border-teal-100 bg-teal-50 px-[6px] py-[1px] text-[12px] font-normal leading-[18px] text-teal-300">
                                      Booked
                                    </span>
                                  ) : (
                                    <VaccineTableRadio
                                      label={`Book vaccine service for ${vac.name}`}
                                      selected={rowSelection[vac.name] === "book"}
                                      disabled={followUpHasAnyUpdateSelection}
                                      onToggle={() =>
                                        setRowChoice(vac.name, "book")
                                      }
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>

                        <tfoot className="sticky bottom-0 z-10">
                          <tr className="bg-gray-20">
                            <td
                              colSpan={2}
                              className="py-3 pl-4 text-gray-600 text-sm font-medium text-left align-middle border-t border-gray-100"
                            >
                              <div>
                                Reference: Canadian Immunization Guide
                                <br />
                                Date modified: 2024-05-03
                              </div>
                            </td>
                            <td
                              colSpan={2}
                              className="border-t border-l border-gray-100 bg-gray-10 py-3 px-6 align-middle"
                            >
                              <div className="flex min-h-[52px] w-full items-center justify-center">
                                {showFollowUpUpdateContinue ? (
                                  <button
                                    type="button"
                                    className="inline-flex h-[52px] w-full max-w-[450px] items-center justify-center rounded-[4px] bg-teal-75 px-8 py-3 text-base font-semibold leading-6 text-teal-300 transition-colors hover:bg-teal-100"
                                    onClick={handleUpdateAndContinue}
                                  >
                                    Update & Continue
                                  </button>
                                ) : showFollowUpSaveContinue ? (
                                  <button
                                    type="button"
                                    className="inline-flex h-[52px] w-full max-w-[450px] items-center justify-center rounded-[4px] bg-REELTeal px-8 py-3 text-base font-semibold leading-6 text-white transition-colors hover:bg-teal-200"
                                    onClick={handleSaveAndContinue}
                                  >
                                    Book Appointment
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmationModalNew
          className="!w-[630px] !max-w-[630px] !min-w-[630px] !max-h-[720px] !gap-0 !overflow-hidden"
          open={showConfirmModalAddVaccine}
          title={"Confirm Vaccine for Appointment"}
          type={"primary"}
          confirmText={"Confirm & Continue"}
          cancelText={"Cancel"}
          headClassName="!px-8 !pt-8 !pb-4"
          titleClassName="!text-[24px] !leading-[29px]"
          contentWrapperClassName="!px-0"
          contentClassName="!pb-0"
          actionsClassName="!px-8 !py-6 !gap-3"
          buttonClassName="!h-[52px] !w-full !min-w-0 !rounded-[4px] !px-6 !py-[14px] !text-base !leading-6 !font-semibold"
          content={() => (
            <div className="flex flex-col text-gray-800 ">
              <div className="flex flex-col gap-[15px] px-8 py-[10px]">
                <div className="bg-gray-10 px-[10px] py-[10px] text-lg font-semibold leading-7 text-gray-800">
                  Scheduler is coming soon to healthcompassRx!
                </div>
                <div className="text-base font-medium leading-6 text-gray-800">
                  <p>
                    Book appointments using the pharmacy&apos;s scheduler at the
                    end of service.
                  </p>
                  <p className="mt-[14px] text-teal-300">
                    Appointments booked through our platform will have reminders
                    automatically added to the storyboard.
                  </p>
                </div>
              </div>
              <div className="flex flex-col px-8">
                {getVaccinesForConfirmModal().map((vaccineName, index) => {
                  const modalDoseType =
                    vaccineModalSelections[vaccineName]?.vaccineDoseType;
                  const initialSeriesTipId = `vaccine-appt-initial-series-${index}`;
                  const boosterDoseTypeTipId = `vaccine-appt-booster-dose-type-${index}`;
                  const doseTypeValueEndAdornment =
                    isInitialSeriesDoseTypeValue(modalDoseType) ? (
                      renderVaccineAppointmentTooltip(
                        initialSeriesTipId,
                        VACCINE_APPOINTMENT_INITIAL_SERIES_TOOLTIP
                      )
                    ) : isBoosterDoseTypeValue(modalDoseType) ? (
                      renderVaccineAppointmentTooltip(
                        boosterDoseTypeTipId,
                        VACCINE_APPOINTMENT_BOOSTER_ORDINAL_TOOLTIP
                      )
                    ) : null;

                  return (
                  <div key={index} className="flex flex-col gap-3 py-5">
                    <div className="rounded-[4px] border border-gray-100 bg-gray-10 px-3 py-3">
                      <span className="text-base font-semibold leading-6 text-gray-800">
                        {vaccineName}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <VaccineAppointmentDoseTypeSelect
                        onChange={(value) => {
                          const newType = value?.value || value?.label || "";
                          setVaccineModalSelections((prev) => {
                            const cur = prev[vaccineName] || {};
                            const next = {
                              ...cur,
                              vaccineDoseType: newType,
                            };
                            if (isBoosterDoseTypeValue(newType)) {
                              next.totalNumberOfVaccineDoses = Array.isArray(
                                cur.totalNumberOfVaccineDoses
                              )
                                ? cur.totalNumberOfVaccineDoses
                                : [];
                            } else if (isSingleDoseTypeValue(newType)) {
                              next.totalNumberOfVaccineDoses = "1";
                            } else {
                              next.totalNumberOfVaccineDoses = "";
                            }
                            return { ...prev, [vaccineName]: next };
                          });
                        }}
                        options={vaccineDoseTypeSuggestionOptions.map((option) => ({
                          label: getVaccineDoseTypeOptionLabel(option),
                          value: option,
                        }))}
                        label="Vaccine Dose Type"
                        value={
                          vaccineModalSelections[vaccineName]?.vaccineDoseType
                            ? {
                                label: getVaccineDoseTypeOptionLabel(
                                  vaccineModalSelections[vaccineName]
                                    ?.vaccineDoseType
                                ),
                                value:
                                  vaccineModalSelections[vaccineName]
                                    ?.vaccineDoseType,
                              }
                            : null
                        }
                        className="w-full h-full [&>div]:!h-full [&>div]:!min-h-[78px] [&>div]:!border-gray-100 [&>div]:!bg-white [&>div]:!px-3 [&>div]:!py-3 [&_button>div>span:last-child]:mr-2"
                        labelClassName="!mb-1 !text-[12px] !font-normal !leading-[18px] !text-gray-800"
                        isRequired={true}
                        valueEndAdornment={doseTypeValueEndAdornment}
                      />
                      {isBoosterDoseTypeValue(
                        vaccineModalSelections[vaccineName]?.vaccineDoseType
                      ) ? (
                        <div className="flex h-full flex-col">
                          <CustomInput
                          name={`${vaccineName}-booster-series`}
                          label={getDoseCountLabel(vaccineName)}
                          type="multi-dropdown"
                          options={boosterSeriesMultiSelectOptions}
                          className="w-full h-full !min-h-[78px]"
                          placeholder="Booster dose"
                          value={
                            Array.isArray(
                              vaccineModalSelections[vaccineName]
                                ?.totalNumberOfVaccineDoses
                            )
                              ? vaccineModalSelections[vaccineName]
                                  .totalNumberOfVaccineDoses
                              : []
                          }
                          onChange={(selectedValues) =>
                            setVaccineModalSelections((prev) => ({
                              ...prev,
                              [vaccineName]: {
                                ...(prev[vaccineName] || {}),
                                totalNumberOfVaccineDoses:
                                  normalizeBoosterOrdinalsSelection(
                                    selectedValues
                                  ),
                              },
                            }))
                          }
                          required
                        />
                        </div>
                      ) : (
                        <CommonSelect
                          onChange={(value) =>
                            setVaccineModalSelections((prev) => ({
                              ...prev,
                              [vaccineName]: {
                                ...(prev[vaccineName] || {}),
                                totalNumberOfVaccineDoses:
                                  value?.value || value?.label || "",
                              },
                            }))
                          }
                          isCreatable={true}
                          options={mergedPrimaryDoseCountOptions}
                          label={getDoseCountLabel(vaccineName)}
                          value={
                            vaccineModalSelections[vaccineName]
                              ?.totalNumberOfVaccineDoses &&
                            !Array.isArray(
                              vaccineModalSelections[vaccineName]
                                ?.totalNumberOfVaccineDoses
                            )
                              ? {
                                  label: String(
                                    vaccineModalSelections[vaccineName]
                                      .totalNumberOfVaccineDoses
                                  ),
                                  value: String(
                                    vaccineModalSelections[vaccineName]
                                      .totalNumberOfVaccineDoses
                                  ),
                                }
                              : null
                          }
                          className="w-full h-full [&>div]:!h-full [&>div]:!min-h-[78px] [&>div]:!border-gray-100 [&>div]:!bg-white [&>div]:!px-3 [&>div]:!py-3 [&_button>div>span:last-child]:mr-2"
                          labelClassName="!mb-1 !text-[12px] !font-normal !leading-[18px] !text-gray-800"
                          isRequired={
                            !isSingleDoseTypeValue(
                              vaccineModalSelections[vaccineName]?.vaccineDoseType
                            )
                          }
                          disabled={isSingleDoseTypeValue(
                            vaccineModalSelections[vaccineName]?.vaccineDoseType
                          )}
                        />
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          onCross={() => {
            setShowConfirmModalAddVaccine(false);
          }}
          handleClose={() => {
            handleCancelAddingVaccine();
          }}
          confirmAction={() => {
            handleAddVaccineToService();
          }}
        />

        {(fromHealthAndWellness || showBack) && (
          <MedicationCreate
            key={`vaccine-amr-${vaccineUpdateIndex}-${vaccineUpdateQueue.join("|")}`}
            open={showVaccineUpdateSlider}
            patientId={id}
            create={true}
            type="Medication"
            selectedDrugType="Vaccine"
            vaccineAnnualReviewLayout={true}
            informationStep={false}
            step={
              vaccineUpdateQueue.length > 0 ? vaccineUpdateIndex + 1 : 1
            }
            maxSteps={Math.max(1, vaccineUpdateQueue.length)}
            title={
              vaccineUpdateQueue[vaccineUpdateIndex]
                ? `Vaccine — ${vaccineUpdateQueue[vaccineUpdateIndex]}`
                : "Vaccine"
            }
            showSaveAndAddAnother={true}
            closeOnSave={
              vaccineUpdateQueue.length === 0 ||
              vaccineUpdateIndex >= vaccineUpdateQueue.length - 1
            }
            customSubmit={false}
            onSkip={() => {}}
            onClose={() => {
              setShowVaccineUpdateSlider(false);
              setVaccineUpdateQueue([]);
              setVaccineUpdateIndex(0);
            }}
            onSave={(name, meta) => {
              if (meta?.saveAndAddAnother) {
                return;
              }
              handleVaccineMedicationSave();
            }}
          />
        )}
      </div>
      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            disabled={showBack}
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => onPrevious()}
          />
          <Button
            type="primary"
            disabled={fromHealthAndWellness && isNextDisabled}
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              (!completeSubStep || (fromHealthAndWellness && isNextDisabled)) && "opacity-40 pointer-events-none"
            }`}
            onClick={() => onNext()}
          />
        </div>
      </div>
    </div>
  );
};

export default VaccineCards;